import * as XLSX from 'xlsx'

function arrayBufferToBase64(buf: ArrayBuffer): string {
  let binary = ''
  const bytes = new Uint8Array(buf)
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(binary)
}

async function fetchPublicFontBase64(path: string): Promise<string> {
  const res = await fetch(path)
  if (!res.ok) throw new Error(`Font yüklenemedi: ${path} (${res.status})`)
  return arrayBufferToBase64(await res.arrayBuffer())
}

export type ExportColumn<T> = {
  key: keyof T
  label: string
}

export function exportRowsToExcel<T extends Record<string, unknown>>(
  rows: T[],
  columns: ExportColumn<T>[],
  fileNameWithoutExt: string
) {
  const exportRows = rows.map((row) => {
    const out: Record<string, unknown> = {}
    for (const c of columns) out[c.label] = row[c.key]
    return out
  })
  const ws = XLSX.utils.json_to_sheet(exportRows)
  const wb = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(wb, ws, 'Veriler')
  XLSX.writeFile(wb, `${fileNameWithoutExt}.xlsx`)
}

export function exportRowsToPdfPrint<T extends Record<string, unknown>>(
  rows: T[],
  columns: ExportColumn<T>[],
  title: string
) {
  void (async () => {
    const [{ jsPDF }, autoTableModule] = await Promise.all([import('jspdf'), import('jspdf-autotable')])
    const autoTable = autoTableModule.default
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

    const variableFileName = 'Outfit-Variable.ttf'
    const boldFileName = 'Outfit-Bold.ttf'
    const [variableBase64, boldBase64] = await Promise.all([
      fetchPublicFontBase64(`/fonts/${variableFileName}`),
      fetchPublicFontBase64(`/fonts/${boldFileName}`),
    ])

    doc.addFileToVFS(variableFileName, variableBase64)
    doc.addFileToVFS(boldFileName, boldBase64)
    doc.addFont(variableFileName, 'Outfit', 'normal')
    doc.addFont(boldFileName, 'Outfit', 'bold')
    doc.setFont('Outfit', 'bold')
    doc.setTextColor(17, 24, 39)
    doc.setFontSize(14)
    doc.text(title, 24, 30)

    const head = [columns.map((c) => c.label)]
    const body = rows.map((row) => columns.map((c) => String(row[c.key] ?? '')))

    autoTable(doc, {
      startY: 40,
      head,
      body,
      margin: { left: 24, right: 24 },
      styles: {
        font: 'Outfit',
        fontStyle: 'bold',
        fontSize: 10.5,
        cellPadding: 5,
        textColor: [17, 24, 39],
        lineColor: [148, 163, 184],
        lineWidth: 0.35,
      },
      headStyles: {
        fillColor: [37, 99, 235],
        textColor: 255,
        font: 'Outfit',
        fontStyle: 'bold',
        fontSize: 10,
      },
      bodyStyles: {
        font: 'Outfit',
        fontStyle: 'bold',
        fontSize: 10,
        textColor: [17, 24, 39],
      },
      theme: 'grid',
    })

    const stamp = new Date().toISOString().slice(0, 10)
    const fileBase = title
      .toLocaleLowerCase('tr')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-ığüşöçİĞÜŞÖÇ]/g, '')

    doc.save(`${fileBase || 'rapor'}-${stamp}.pdf`)
  })().catch((err) => {
    console.error('PDF oluşturulurken hata oluştu:', err)
  })
}
